import { Lock, User as UserIcon, Building2, AlertCircle, CheckCircle, ArrowUp, ArrowDown, Key, Copy, RefreshCw, Car, Trash2, Tag } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import type { User, Tenant, Driver, UserRole, TenantPriceRule, TenantPriceRuleCreateRequest } from '@/types'
import { tenantApi } from '@/api'

const AIRPORTS = ['桃園機場一航廈', '桃園機場二航廈', '松山機場', '台中機場', '高雄機場', '基隆港']
import { driverApi } from '@/api/drivers'
import apiClient from '@/api/client'
import { leaveApi, LeaveType } from '@/api/leaves'
import { groupRegionsByCounty, sortByPriority, OTHER_COUNTY, type RegionOrder, EMPTY_REGION_ORDER } from '@/utils/taiwanRegions'

// DEBUG: 幫助調試設置頁面功能
// 打開瀏覽器開發者工具 (F12)，在控制台看以下日誌：
// 1. 從 API 載入的租戶資料 - 確認租戶 ID 和初始數據
// 2. 準備發送的數據 - 檢查要提交的數據格式
// 3. 租戶 ID - 驗證租戶 ID 是否正確
// 4. API 返回結果 - 確認保存成功
// 5. 重新加載的租戶資訊 - 驗證數據已保存

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'tenant' | 'priceRules'>('profile')
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [driverInfo, setDriverInfo] = useState<Driver | null>(null)

  // Profile Form
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
  })

  // Password Form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // Tenant Config Form
  const [tenantForm, setTenantForm] = useState({
    short_name: '',
    vehicle_categories: [] as Array<{ name: string; order?: number; image_url?: string; description?: string }>,
    payment_methods: [] as Array<{ name: string; order?: number }>,
    vendors: [] as Array<{ name: string }>,
    show_vendors: false,
    enable_batch_orders: false,
    dashboard_calendar_first: false,
    show_google_calendar_button: true,
    enable_xunan_sign: false,
    enable_fee_split: false,
    fee_split_vendor_percent: 10,
    enable_extended_categories: false,
    enable_wait_extra: false,
    enable_second_vendor_rebate: false,
    enable_member_booking: false,
    member_edit_hours: 24,
    document_templates: '',
    external_api_key: '',
    hide_dispatch_amount: false,
    show_driver_fee_info: false,
    pricing_is_tax_inclusive_default: true,
    name_board_price: 0,
    child_seat_price: 0,
    booster_seat_price: 0,
    airport_pickup_surcharge: 100,
    extra_passenger_surcharge: 100,
  })
  const [apiKeyCopied, setApiKeyCopied] = useState(false)
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [newLeaveTypeName, setNewLeaveTypeName] = useState('')
  const [newLeaveTypeColor, setNewLeaveTypeColor] = useState('#6366f1')
  const [leaveTypeSubmitting, setLeaveTypeSubmitting] = useState(false)
  const [priceRules, setPriceRules] = useState<TenantPriceRule[]>([])
  const [newPriceRule, setNewPriceRule] = useState<TenantPriceRuleCreateRequest>({ airport: AIRPORTS[0], region: '', vehicle_type: '', price: 0 })
  const [applyBothTerminals, setApplyBothTerminals] = useState(false)
  const [priceRuleSubmitting, setPriceRuleSubmitting] = useState(false)
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)
  const [editingRule, setEditingRule] = useState<TenantPriceRuleCreateRequest>({ airport: '', region: '', vehicle_type: '', price: 0 })
  const [editingRuleSubmitting, setEditingRuleSubmitting] = useState(false)
  const [importingCsv, setImportingCsv] = useState(false)
  const [importAirports, setImportAirports] = useState<string[]>([])
  const [priceFilterAirport, setPriceFilterAirport] = useState('')
  const [priceFilterRegion, setPriceFilterRegion] = useState('')
  const [priceFilterVehicleType, setPriceFilterVehicleType] = useState('')
  const [importResult, setImportResult] = useState<{ created: number; updated: number; errors: string[] } | null>(null)
  const [regionOrder, setRegionOrder] = useState<RegionOrder>(EMPTY_REGION_ORDER)
  const [regionOrderCounty, setRegionOrderCounty] = useState('')
  const csvInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const loadUserData = async () => {
      try {
        console.log('=== 開始載入設置頁面數據 ===')

        // 從 localStorage 取得目前使用者資訊
        const userStr = localStorage.getItem('user')
        const tenantStr = localStorage.getItem('tenant')

        console.log('localStorage 中的用戶:', userStr ? JSON.parse(userStr) : null)
        console.log('localStorage 中的租戶:', tenantStr ? JSON.parse(tenantStr) : null)

        if (userStr) {
          const user = JSON.parse(userStr)
          setCurrentUser(user)
          setProfileForm({
            name: user.name,
            email: user.email,
            phone: user.phone,
          })
          if (user.driver_id) {
            try {
              const driver = await driverApi.getDriverById(user.driver_id)
              setDriverInfo(driver)
            } catch {
              // ignore
            }
          }
        }

        // 從 localStorage 取得租戶資訊，然後從 API 更新最新資料
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr)
          console.log('從 localStorage 解析的租戶物件:', tenant)
          console.log('租戶 ID:', tenant.id)

          setCurrentTenant(tenant)

          try {
            // 從 API 取得租戶詳情（包括最新的車輛類別和付款方式）
            console.log('準備調用 API 獲取租戶詳情，ID:', tenant.id)
            const updatedTenant = await tenantApi.get(tenant.id)
            console.log('從 API 載入的租戶資料:', updatedTenant)
            setCurrentTenant(updatedTenant)
            
            // 解析 JSON 字串為陣列
            const parseJsonField = (value: any): any[] => {
              if (Array.isArray(value)) return value;
              if (typeof value === 'string') {
                try {
                  return JSON.parse(value);
                } catch {
                  return [];
                }
              }
              return [];
            };
            
            setTenantForm({
              short_name: updatedTenant.short_name || '',
              vehicle_categories: parseJsonField(updatedTenant.vehicle_categories),
              payment_methods: parseJsonField(updatedTenant.payment_methods),
              vendors: parseJsonField(updatedTenant.form_config?.vendors ?? []),
              show_vendors: updatedTenant.form_config?.show_vendors ?? false,
              enable_batch_orders: updatedTenant.form_config?.enable_batch_orders ?? false,
              document_templates: typeof updatedTenant.document_templates === 'string' ? updatedTenant.document_templates : JSON.stringify(updatedTenant.document_templates || {}),
              external_api_key: updatedTenant.form_config?.external_api_key || '',
              hide_dispatch_amount: updatedTenant.form_config?.hide_dispatch_amount ?? false,
              show_driver_fee_info: updatedTenant.form_config?.show_driver_fee_info ?? false,
              pricing_is_tax_inclusive_default: updatedTenant.form_config?.pricing_is_tax_inclusive_default ?? true,
              name_board_price: updatedTenant.form_config?.name_board_price ?? 0,
              child_seat_price: updatedTenant.form_config?.child_seat_price ?? 0,
              booster_seat_price: updatedTenant.form_config?.booster_seat_price ?? 0,
              airport_pickup_surcharge: updatedTenant.form_config?.airport_pickup_surcharge ?? 100,
              extra_passenger_surcharge: updatedTenant.form_config?.extra_passenger_surcharge ?? 100,
              dashboard_calendar_first: updatedTenant.form_config?.dashboard_calendar_first ?? false,
              show_google_calendar_button: updatedTenant.form_config?.show_google_calendar_button ?? true,
              enable_xunan_sign: updatedTenant.form_config?.enable_xunan_sign ?? false,
              enable_fee_split: updatedTenant.form_config?.enable_fee_split ?? false,
              fee_split_vendor_percent: updatedTenant.form_config?.fee_split_vendor_percent ?? 10,
              enable_extended_categories: updatedTenant.form_config?.enable_extended_categories ?? false,
              enable_wait_extra: updatedTenant.form_config?.enable_wait_extra ?? false,
              enable_second_vendor_rebate: updatedTenant.form_config?.enable_second_vendor_rebate ?? false,
              enable_member_booking: updatedTenant.form_config?.enable_member_booking ?? false,
              member_edit_hours: updatedTenant.form_config?.member_edit_hours ?? 24,
            })
            setRegionOrder(updatedTenant.form_config?.region_order ?? EMPTY_REGION_ORDER)
          } catch (error: any) {
            console.error('從 API 載入租戶資訊失敗:', error)
            console.error('錯誤詳情:', error.response?.data || error.message)
            // 使用 localStorage 的備援資料
            // 解析 JSON 字串為陣列
            const parseJsonField = (value: any): any[] => {
              if (Array.isArray(value)) return value;
              if (typeof value === 'string') {
                try {
                  return JSON.parse(value);
                } catch {
                  return [];
                }
              }
              return [];
            };
            setTenantForm({
              short_name: tenant.short_name || '',
              vehicle_categories: parseJsonField(tenant.vehicle_categories),
              payment_methods: parseJsonField(tenant.payment_methods),
              vendors: parseJsonField(tenant.form_config?.vendors ?? []),
              show_vendors: tenant.form_config?.show_vendors ?? false,
              enable_batch_orders: tenant.form_config?.enable_batch_orders ?? false,
              document_templates: typeof tenant.document_templates === 'string' ? tenant.document_templates : JSON.stringify(tenant.document_templates || {}),
              external_api_key: tenant.form_config?.external_api_key || '',
              hide_dispatch_amount: tenant.form_config?.hide_dispatch_amount ?? false,
              show_driver_fee_info: tenant.form_config?.show_driver_fee_info ?? false,
              pricing_is_tax_inclusive_default: tenant.form_config?.pricing_is_tax_inclusive_default ?? true,
              name_board_price: tenant.form_config?.name_board_price ?? 0,
              child_seat_price: tenant.form_config?.child_seat_price ?? 0,
              booster_seat_price: tenant.form_config?.booster_seat_price ?? 0,
              airport_pickup_surcharge: tenant.form_config?.airport_pickup_surcharge ?? 100,
              extra_passenger_surcharge: tenant.form_config?.extra_passenger_surcharge ?? 100,
              dashboard_calendar_first: tenant.form_config?.dashboard_calendar_first ?? false,
              show_google_calendar_button: tenant.form_config?.show_google_calendar_button ?? true,
              enable_xunan_sign: tenant.form_config?.enable_xunan_sign ?? false,
              enable_fee_split: tenant.form_config?.enable_fee_split ?? false,
              fee_split_vendor_percent: tenant.form_config?.fee_split_vendor_percent ?? 10,
              enable_extended_categories: tenant.form_config?.enable_extended_categories ?? false,
              enable_wait_extra: tenant.form_config?.enable_wait_extra ?? false,
              enable_second_vendor_rebate: tenant.form_config?.enable_second_vendor_rebate ?? false,
              enable_member_booking: tenant.form_config?.enable_member_booking ?? false,
              member_edit_hours: tenant.form_config?.member_edit_hours ?? 24,
            })
            setRegionOrder(tenant.form_config?.region_order ?? EMPTY_REGION_ORDER)
          }
        } else {
          console.warn('localStorage 中沒有租戶信息')
        }
      } catch (error) {
        console.error('載入使用者資訊失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserData()

    const fetchLeaveTypes = async () => {
      try {
        const types = await leaveApi.listLeaveTypes()
        setLeaveTypes(types)
      } catch {
        // ignore
      }
    }
    fetchLeaveTypes()
  }, [])

  useEffect(() => {
    if (!currentTenant?.id) return
    tenantApi.getPriceRules(currentTenant.id).then(setPriceRules).catch(() => {})
  }, [currentTenant?.id])

  const handleClearCache = async () => {
    if (!confirm('確定要清除快取並重新載入嗎？')) return
    try {
      if ('caches' in window) {
        const keys = await caches.keys()
        await Promise.all(keys.map(key => caches.delete(key)))
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map(reg => reg.unregister()))
      }
    } catch (error) {
      console.error('清除快取失敗:', error)
    } finally {
      window.location.reload()
    }
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setSubmitting(true)
    try {
      if (currentUser) {
        const updatedUser = { ...currentUser, ...profileForm }
        localStorage.setItem('user', JSON.stringify(updatedUser))
        setCurrentUser(updatedUser)
      }
      setMessage({ type: 'success', text: '個人資訊已儲存' })
    } catch (error) {
      setMessage({ type: 'error', text: '儲存失敗，請重試' })
    } finally {
      setSubmitting(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: '新密碼與確認密碼不一致' })
      return
    }

    if (passwordForm.new_password.length < 6) {
      setMessage({ type: 'error', text: '新密碼長度至少 6 個字元' })
      return
    }

    setSubmitting(true)
    try {
      await apiClient.post('/users/me/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      })
      setMessage({ type: 'success', text: '密碼已變更' })
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      })
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || '密碼變更失敗，請檢查當前密碼' })
    } finally {
      setSubmitting(false)
    }
  }

  // 上下移動列表項目的輔助函數
  const moveVehicleCategory = (index: number, direction: 'up' | 'down') => {
    const categories = [...tenantForm.vehicle_categories]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= categories.length) return

    [categories[index], categories[newIndex]] = [categories[newIndex], categories[index]]
    setTenantForm({ ...tenantForm, vehicle_categories: categories })
  }

  const movePaymentMethod = (index: number, direction: 'up' | 'down') => {
    const methods = [...tenantForm.payment_methods]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= methods.length) return

    [methods[index], methods[newIndex]] = [methods[newIndex], methods[index]]
    setTenantForm({ ...tenantForm, payment_methods: methods })
  }

  const moveVendor = (index: number, direction: 'up' | 'down') => {
    const vendors = [...tenantForm.vendors]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= vendors.length) return

    [vendors[index], vendors[newIndex]] = [vendors[newIndex], vendors[index]]
    setTenantForm({ ...tenantForm, vendors })
  }


  const handleTenantSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setMessage(null)

    if (!currentTenant) {
      setMessage({ type: 'error', text: '租戶資訊未載入' })
      return
    }

    setSubmitting(true)
    try {
      const existingFormConfig = currentTenant.form_config || {}
      const updateData = {
        short_name: tenantForm.short_name,
        vehicle_categories: tenantForm.vehicle_categories,
        payment_methods: tenantForm.payment_methods,
        document_templates: tenantForm.document_templates ? JSON.parse(tenantForm.document_templates) : null,
        form_config: {
          ...existingFormConfig,
          external_api_key: tenantForm.external_api_key || undefined,
          hide_dispatch_amount: tenantForm.hide_dispatch_amount,
          show_driver_fee_info: tenantForm.show_driver_fee_info,
          pricing_is_tax_inclusive_default: tenantForm.pricing_is_tax_inclusive_default,
          vendors: tenantForm.vendors,
          show_vendors: tenantForm.show_vendors,
          enable_batch_orders: tenantForm.enable_batch_orders,
          name_board_price: tenantForm.name_board_price,
          child_seat_price: tenantForm.child_seat_price,
          booster_seat_price: tenantForm.booster_seat_price,
          airport_pickup_surcharge: tenantForm.airport_pickup_surcharge,
          extra_passenger_surcharge: tenantForm.extra_passenger_surcharge,
          dashboard_calendar_first: tenantForm.dashboard_calendar_first,
          show_google_calendar_button: tenantForm.show_google_calendar_button,
          enable_xunan_sign: tenantForm.enable_xunan_sign,
          enable_fee_split: tenantForm.enable_fee_split,
          fee_split_vendor_percent: tenantForm.fee_split_vendor_percent,
          enable_extended_categories: tenantForm.enable_extended_categories,
          enable_wait_extra: tenantForm.enable_wait_extra,
          enable_second_vendor_rebate: tenantForm.enable_second_vendor_rebate,
          enable_member_booking: tenantForm.enable_member_booking,
          member_edit_hours: tenantForm.member_edit_hours,
          region_order: regionOrder,
        },
      }

      const result = await tenantApi.update(currentTenant.id, updateData)

      const messages = ['租戶設定已儲存']
      if (tenantForm.hide_dispatch_amount !== (currentTenant.form_config?.hide_dispatch_amount ?? false)) {
        messages.push(tenantForm.hide_dispatch_amount ? '派車單金額已隱藏' : '派車單金額已顯示')
      }
      setMessage({ type: 'success', text: messages[messages.length - 1] })
      const updatedTenant = await tenantApi.get(currentTenant.id)
      setCurrentTenant(updatedTenant)
      localStorage.setItem('tenant', JSON.stringify(updatedTenant))
    } catch (error: any) {
      setMessage({ type: 'error', text: '儲存失敗，請重試' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">載入中...</div>
      </div>
    )
  }

  const filteredPriceRules = priceRules.filter(r =>
    (!priceFilterAirport || r.airport === priceFilterAirport) &&
    (!priceFilterRegion || r.region.includes(priceFilterRegion)) &&
    (!priceFilterVehicleType || (priceFilterVehicleType === '__generic__' ? !r.vehicle_type : r.vehicle_type === priceFilterVehicleType))
  )
  const priceRuleVehicleTypeOptions = [...new Set(priceRules.map(r => r.vehicle_type).filter((v): v is string => !!v))]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">設定</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            管理你的帳戶和租戶設定
          </p>
        </div>
        <button
          type="button"
          onClick={handleClearCache}
          title="若功能更新後手機看不到最新版本，可點此清除快取"
          className="flex items-center gap-2 rounded-lg border border-amber-500 bg-amber-400 px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-500 transition-colors shrink-0"
        >
          <Trash2 className="h-4 w-4" /> 清除快取及重新載入
        </button>
      </div>

      {message && (
        <div className={`flex items-center gap-3 rounded-lg p-4 ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5 flex-shrink-0" /> : <AlertCircle className="h-5 w-5 flex-shrink-0" />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button onClick={() => setActiveTab('profile')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'profile' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
          <UserIcon className="h-4 w-4" /> 個人資訊
        </button>
        <button onClick={() => setActiveTab('password')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'password' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
          <Lock className="h-4 w-4" /> 變更密碼
        </button>
        {currentUser?.role !== 'driver' && (
          <button onClick={() => setActiveTab('tenant')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'tenant' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
            <Building2 className="h-4 w-4" /> 租戶設定
          </button>
        )}
        {currentUser?.role !== 'driver' && (
          <button onClick={() => setActiveTab('priceRules')} className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'priceRules' ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400' : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}>
            <Tag className="h-4 w-4" /> 公用訂車牌價
          </button>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="space-y-4 max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">個人資訊</h2>

            {/* 姓名 + 電話同一列 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">姓名</label>
                <input type="text" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">電話</label>
                <input type="tel" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">電子郵件</label>
              <input type="email" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>

            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '儲存中...' : '儲存修改'}</button>

            {/* 綁定車輛資訊（司機才顯示） */}
            {driverInfo && (() => {
              const allVehicles = [
                { plate: driverInfo.vehicle_plate, type: driverInfo.vehicle_type, brand_model: driverInfo.vehicle_brand_model, color: driverInfo.vehicle_color },
                ...((driverInfo.vehicles || []) as { plate: string; type?: string; brand_model?: string; color?: string }[])
                  .filter(v => v.plate && v.plate !== driverInfo.vehicle_plate),
              ]
              return (
                <div className="mt-6 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-5">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <Car className="h-4 w-4 text-gray-500" />
                    綁定車輛
                    <span className="text-xs font-normal text-gray-400">（{allVehicles.length} 輛）</span>
                  </h3>
                  <div className="space-y-2">
                    {allVehicles.map((v, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                        <span className="font-mono font-bold text-gray-900 dark:text-white text-sm">{v.plate}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">|</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{v.type || '-'}</span>
                        {v.brand_model && <><span className="text-gray-500 dark:text-gray-400 text-xs">|</span><span className="text-sm text-gray-600 dark:text-gray-400">{v.brand_model}</span></>}
                        {v.color && <span className="ml-auto text-xs text-gray-400">{v.color}</span>}
                        {i === 0 && <span className="ml-auto text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">主車</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">變更密碼</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">當前密碼</label>
              <input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">新密碼</label>
              <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">確認新密碼</label>
              <input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '變更中...' : '變更密碼'}</button>
          </form>
        )}

        {activeTab === 'tenant' && currentUser?.role !== 'driver' && (
          <form onSubmit={handleTenantSubmit} className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">租戶設定 - {currentTenant?.name}</h2>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">商戶短名稱</label>
                {currentTenant?.tax_id && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    統一編號（司機預設密碼）：<span className="font-mono font-medium text-gray-700 dark:text-gray-300">{currentTenant.tax_id.trim()}</span>
                  </span>
                )}
              </div>
              <input type="text" value={tenantForm.short_name} onChange={(e) => setTenantForm({ ...tenantForm, short_name: e.target.value })} placeholder="例：台北分公司" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">車輛類別</h3>
                <div className="space-y-2">
                  {tenantForm.vehicle_categories.map((cat, idx) => (
                    <div key={idx} className="space-y-1 rounded-lg border border-gray-100 dark:border-gray-700 p-2">
                      <div className="flex gap-1">
                        <input type="text" value={cat.name} onChange={(e) => { const updated = [...tenantForm.vehicle_categories]; updated[idx].name = e.target.value; setTenantForm({ ...tenantForm, vehicle_categories: updated }) }} placeholder="例：轎車、SUV" className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                        <button type="button" onClick={() => moveVehicleCategory(idx, 'up')} disabled={idx === 0} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="上移"><ArrowUp className="h-4 w-4" /></button>
                        <button type="button" onClick={() => moveVehicleCategory(idx, 'down')} disabled={idx === tenantForm.vehicle_categories.length - 1} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="下移"><ArrowDown className="h-4 w-4" /></button>
                        <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vehicle_categories: tenantForm.vehicle_categories.filter((_, i) => i !== idx) }) }} className="rounded-lg bg-red-100 px-2 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors text-xs">移除</button>
                      </div>
                      <input
                        type="text"
                        value={cat.image_url || ''}
                        onChange={(e) => { const updated = [...tenantForm.vehicle_categories]; updated[idx].image_url = e.target.value; setTenantForm({ ...tenantForm, vehicle_categories: updated }) }}
                        placeholder="車輛圖片網址（選填，V2 訂車表單車型說明用）"
                        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      />
                      <input
                        type="text"
                        value={cat.description || ''}
                        onChange={(e) => { const updated = [...tenantForm.vehicle_categories]; updated[idx].description = e.target.value; setTenantForm({ ...tenantForm, vehicle_categories: updated }) }}
                        placeholder="車型說明（選填，例：4人座、大行李箱可放2件）"
                        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      />
                    </div>
                  ))}
                  <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vehicle_categories: [...tenantForm.vehicle_categories, { name: '', order: tenantForm.vehicle_categories.length }] }) }} className="w-full rounded-lg border border-blue-300 bg-blue-50 py-2 text-sm text-blue-600 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors">+ 新增車輛類別</button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">廠商管理</h3>
                <div className="space-y-2">
                  {tenantForm.vendors.map((vendor, idx) => (
                    <div key={idx} className="flex gap-1">
                      <input type="text" value={vendor.name} onChange={(e) => { const updated = [...tenantForm.vendors]; updated[idx].name = e.target.value; setTenantForm({ ...tenantForm, vendors: updated }) }} placeholder="例：大榮旅遊" className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                      <button type="button" onClick={() => moveVendor(idx, 'up')} disabled={idx === 0} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="上移"><ArrowUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => moveVendor(idx, 'down')} disabled={idx === tenantForm.vendors.length - 1} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="下移"><ArrowDown className="h-4 w-4" /></button>
                      <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vendors: tenantForm.vendors.filter((_, i) => i !== idx) }) }} className="rounded-lg bg-red-100 px-2 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors text-xs">移除</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => { setTenantForm({ ...tenantForm, vendors: [...tenantForm.vendors, { name: '' }] }) }} className="w-full rounded-lg border border-orange-300 bg-orange-50 py-2 text-sm text-orange-600 hover:bg-orange-100 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50 transition-colors">+ 新增廠商</button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">付款方式</h3>
                <div className="space-y-2">
                  {tenantForm.payment_methods.map((method, idx) => (
                    <div key={idx} className="flex gap-1">
                      <input type="text" value={method.name} onChange={(e) => { const updated = [...tenantForm.payment_methods]; updated[idx].name = e.target.value; setTenantForm({ ...tenantForm, payment_methods: updated }) }} placeholder="例：現金、信用卡" className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                      <button type="button" onClick={() => movePaymentMethod(idx, 'up')} disabled={idx === 0} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="上移"><ArrowUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => movePaymentMethod(idx, 'down')} disabled={idx === tenantForm.payment_methods.length - 1} className="rounded-lg bg-gray-100 px-2 py-2 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors" title="下移"><ArrowDown className="h-4 w-4" /></button>
                      <button type="button" onClick={() => { setTenantForm({ ...tenantForm, payment_methods: tenantForm.payment_methods.filter((_, i) => i !== idx) }) }} className="rounded-lg bg-red-100 px-2 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors text-xs">移除</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => { setTenantForm({ ...tenantForm, payment_methods: [...tenantForm.payment_methods, { name: '', order: tenantForm.payment_methods.length }] }) }} className="w-full rounded-lg border border-green-300 bg-green-50 py-2 text-sm text-green-600 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors">+ 新增付款方式</button>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <Key className="h-4 w-4 text-gray-500" /> 外部 API Key
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                外部系統建立派車單時，在 Header 帶入 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">X-Api-Key: &lt;key&gt;</code>，
                POST 至 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/api/v1/public/external/orders</code>
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tenantForm.external_api_key}
                  onChange={(e) => setTenantForm({ ...tenantForm, external_api_key: e.target.value })}
                  placeholder="尚未設定 API Key"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  readOnly
                />
                <button
                  type="button"
                  title="複製"
                  onClick={() => { navigator.clipboard.writeText(tenantForm.external_api_key); setApiKeyCopied(true); setTimeout(() => setApiKeyCopied(false), 2000) }}
                  disabled={!tenantForm.external_api_key}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                >
                  {apiKeyCopied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  title="產生新 Key"
                  onClick={() => setTenantForm({ ...tenantForm, external_api_key: crypto.randomUUID() })}
                  className="rounded-lg border border-blue-300 px-3 py-2 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400">產生新 Key 後需按「儲存設定」才會生效，舊 Key 立即失效。</p>
            </div>

            {/* 派車單金額顯示 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">派車單設定</h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, hide_dispatch_amount: !tenantForm.hide_dispatch_amount })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    !tenantForm.hide_dispatch_amount ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    !tenantForm.hide_dispatch_amount ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    派車單顯示金額
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      !tenantForm.hide_dispatch_amount
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {!tenantForm.hide_dispatch_amount ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {!tenantForm.hide_dispatch_amount
                      ? '現金顯示金額數字，非現金顯示「-」'
                      : '金額欄位完全隱藏（不管付款方式）'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, show_driver_fee_info: !tenantForm.show_driver_fee_info })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.show_driver_fee_info ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.show_driver_fee_info ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    顯示司機費用／廠商回金
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.show_driver_fee_info
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.show_driver_fee_info ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    管理員列表卡片狀態旁顯示（司機費用/廠商回金）
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, show_vendors: !tenantForm.show_vendors })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.show_vendors ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.show_vendors ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    顯示廠商
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.show_vendors
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.show_vendors ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    建單表單顯示廠商下拉，並記錄廠商回金與已回廠商日期
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_batch_orders: !tenantForm.enable_batch_orders })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_batch_orders ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_batch_orders ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    啟用批次建單
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_batch_orders
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_batch_orders ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後側邊選單出現「批次管理」，可建立週期性訂單範本並批次產生訂單
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, dashboard_calendar_first: !tenantForm.dashboard_calendar_first })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.dashboard_calendar_first ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.dashboard_calendar_first ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    儀表板日曆置頂
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.dashboard_calendar_first
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.dashboard_calendar_first ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後儀表板的日曆區塊會顯示在接案統計圖表上方
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, show_google_calendar_button: !tenantForm.show_google_calendar_button })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.show_google_calendar_button ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.show_google_calendar_button ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    顯示「加入 Google 日曆」按鈕
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.show_google_calendar_button
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.show_google_calendar_button ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後儀表板日曆的訂單卡片會顯示「加入 Google 日曆」按鈕
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_xunan_sign: !tenantForm.enable_xunan_sign })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_xunan_sign ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_xunan_sign ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    啟用「迅安簽單」
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_xunan_sign
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_xunan_sign ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後，新增/編輯派車單表單會出現「迅安簽單」勾選，勾選後派車單會用迅安的樣板格式輸出
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_extended_categories: !tenantForm.enable_extended_categories })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_extended_categories ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_extended_categories ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    派車類別改為「單程／包車」
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_extended_categories
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_extended_categories ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後，新增/編輯派車單與訂車頁的類別選項會從「接機/送機/其他」改成「接機/送機/單程/包車」；關閉時維持原本三個選項不變
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_wait_extra: !tenantForm.enable_wait_extra })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_wait_extra ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_wait_extra ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    顯示「等待時間／追加金額」
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_wait_extra
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_wait_extra ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後，新增/編輯派車單在備註上方會出現「等待時間（分鐘）」與「追加金額」欄位，客戶報表也會顯示這兩欄與追加金額小計
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_second_vendor_rebate: !tenantForm.enable_second_vendor_rebate })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_second_vendor_rebate ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_second_vendor_rebate ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    顯示「第二組廠商回金」
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_second_vendor_rebate
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_second_vendor_rebate ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    開啟後，新增/編輯派車單會多出第二組「廠商／廠商回金／已回廠商」，司機費用自動帶入改為「車資 − 回金一 − 回金二」，廠商回金報表與司機報表也會一併計入；關閉時維持原本一組
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_fee_split: !tenantForm.enable_fee_split })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    tenantForm.enable_fee_split ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_fee_split ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      車資依比例自動填入司機費用／廠商回金
                      <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                        tenantForm.enable_fee_split
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {tenantForm.enable_fee_split ? '開啟' : '關閉'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      開啟後，新增/編輯派車單輸入車資時，會依下面的廠商回金比例自動算好填入司機費用跟廠商回金（例如比例 10%、車資 1000 → 司機費用 900、廠商回金 100），填完仍可手動再調整
                    </div>
                  </div>
                  {tenantForm.enable_fee_split && (
                    <div className="flex items-center gap-2 mt-1">
                      <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">廠商回金比例</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={tenantForm.fee_split_vendor_percent}
                        onChange={(e) => setTenantForm({ ...tenantForm, fee_split_vendor_percent: parseInt(e.target.value) || 0 })}
                        className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 個人會員 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">個人會員</h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setTenantForm({ ...tenantForm, enable_member_booking: !tenantForm.enable_member_booking })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${
                    tenantForm.enable_member_booking ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    tenantForm.enable_member_booking ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    開放個人會員（LINE 登入）自行下單、查詢、修改與取消
                    <span className={`ml-2 text-xs font-normal px-1.5 py-0.5 rounded ${
                      tenantForm.enable_member_booking
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {tenantForm.enable_member_booking ? '開啟' : '關閉'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    會員下的單會直接成立為「待派車」，並出現在訂單與客戶清單。公司客戶原有的訂車連結不受影響。儲存後請把下方會員連結給客人
                  </div>
                </div>
              </div>
              {tenantForm.enable_member_booking && (
                <div className="space-y-2 pl-14">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">會員可自行修改／取消：上車前</label>
                    <input
                      type="number"
                      min={0}
                      value={tenantForm.member_edit_hours}
                      onChange={(e) => setTenantForm({ ...tenantForm, member_edit_hours: Math.max(parseInt(e.target.value) || 0, 0) })}
                      className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">小時以前（之後需聯絡車行）</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!currentTenant) return
                        const token = await tenantApi.getMemberLink(currentTenant.id)
                        const link = `${window.location.origin}/member/${token}`
                        try { await navigator.clipboard.writeText(link) } catch { /* ignore */ }
                        window.prompt('會員連結（已嘗試複製）', link)
                      }}
                      className="rounded-lg border border-blue-300 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 cursor-pointer transition-colors dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      取得會員連結
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">需先儲存設定，連結才會生效</span>
                  </div>
                </div>
              )}
            </div>

            {/* 車資預設設定 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">車資預設</h3>
              <div className="flex items-center gap-3">
                <select
                  value={tenantForm.pricing_is_tax_inclusive_default ? 'true' : 'false'}
                  onChange={(e) => setTenantForm({ ...tenantForm, pricing_is_tax_inclusive_default: e.target.value === 'true' })}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="true">含稅（預設勾選「含稅」）</option>
                  <option value="false">未稅（預設勾選「未稅」）</option>
                </select>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                建單時「車資」旁的「含稅|未稅」按鈕預設狀態
              </div>
            </div>

            {/* 加值項目預設金額 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">加值項目預設金額</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                建單時勾選「舉牌」或選擇兒童座椅/增高墊數量，會自動帶入以下預設金額，仍可依訂單個別調整。
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">舉牌（每筆）</label>
                  <input
                    type="number" min={0}
                    value={tenantForm.name_board_price}
                    onChange={(e) => setTenantForm({ ...tenantForm, name_board_price: parseInt(e.target.value) || 0 })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">兒童座椅（每張）</label>
                  <input
                    type="number" min={0}
                    value={tenantForm.child_seat_price}
                    onChange={(e) => setTenantForm({ ...tenantForm, child_seat_price: parseInt(e.target.value) || 0 })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">增高墊（每張）</label>
                  <input
                    type="number" min={0}
                    value={tenantForm.booster_seat_price}
                    onChange={(e) => setTenantForm({ ...tenantForm, booster_seat_price: parseInt(e.target.value) || 0 })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* 假別管理 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">假別管理</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                設定司機請假時可選用的假別，刪除假別前請確認無相關請假記錄。
              </p>
              <div className="space-y-2">
                {leaveTypes.map((lt) => (
                  <div key={lt.id} className="flex items-center gap-2">
                    <span
                      className="w-5 h-5 rounded flex-shrink-0 border border-gray-200"
                      style={{ backgroundColor: lt.color }}
                    />
                    <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{lt.name}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`確定刪除假別「${lt.name}」？`)) return
                        try {
                          await leaveApi.deleteLeaveType(lt.id)
                          setLeaveTypes((prev) => prev.filter((t) => t.id !== lt.id))
                        } catch {
                          setMessage({ type: 'error', text: '刪除失敗，請確認無相關請假記錄' })
                        }
                      }}
                      className="rounded-lg bg-red-100 px-2 py-1 text-xs text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 transition-colors"
                    >
                      移除
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={newLeaveTypeColor}
                  onChange={(e) => setNewLeaveTypeColor(e.target.value)}
                  className="h-9 w-9 rounded border border-gray-300 cursor-pointer p-0.5"
                  title="選擇顏色"
                />
                <input
                  type="text"
                  value={newLeaveTypeName}
                  onChange={(e) => setNewLeaveTypeName(e.target.value)}
                  placeholder="假別名稱，例：病假"
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  disabled={!newLeaveTypeName.trim() || leaveTypeSubmitting}
                  onClick={async () => {
                    if (!newLeaveTypeName.trim()) return
                    setLeaveTypeSubmitting(true)
                    try {
                      const created = await leaveApi.createLeaveType({
                        name: newLeaveTypeName.trim(),
                        color: newLeaveTypeColor,
                      })
                      setLeaveTypes((prev) => [...prev, created])
                      setNewLeaveTypeName('')
                      setNewLeaveTypeColor('#6366f1')
                    } catch {
                      setMessage({ type: 'error', text: '新增假別失敗' })
                    } finally {
                      setLeaveTypeSubmitting(false)
                    }
                  }}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {leaveTypeSubmitting ? '新增中...' : '+ 新增'}
                </button>
              </div>
            </div>


<div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">文檔樣板配置 (JSON)</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                配置派車單和出租單的樣板檔路徑和欄位座標。除了 dispatch/rental 這兩個預設樣板，也可以在
                <code className="mx-1 px-1 rounded bg-gray-100 dark:bg-gray-700">dispatch_templates</code>
                底下加其他具名派車單樣板，例如 <code className="mx-1 px-1 rounded bg-gray-100 dark:bg-gray-700">{'{"dispatch_templates": {"xunan": {"name": "訊安派車單", "template_path": "...", "fields": {...}}}}'}</code>，
                新增訂單時就能選；圖檔上傳跟座標量測請找工程端協助。
              </p>
              <textarea
                value={tenantForm.document_templates}
                onChange={(e) => setTenantForm({ ...tenantForm, document_templates: e.target.value })}
                placeholder='{"dispatch": {"template_path": "/app/templates/documents/dispatch_template.jpg", ...}, "rental": {...}}'
                className="w-full h-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-mono text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">{submitting ? '儲存中...' : '儲存設定'}</button>
          </form>
        )}

        {activeTab === 'priceRules' && currentUser?.role !== 'driver' && currentTenant && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">公用訂車表單 V2 牌價（機場 / 區域 / 車型 → 價格）</h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              給「公用訂車表單 V2」詢價功能用，跟客戶合約價分開。下面設定的價格一律視為「送機」價，「接機」會自動加上右邊的接機加價金額，不用另外重複設定一次。同一個機場+區域可以針對不同車型設不同價格，車型留空代表該機場+區域的通用價格。
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">接機加價（元）</label>
                <input
                  type="number"
                  min={0}
                  value={tenantForm.airport_pickup_surcharge}
                  onChange={(e) => setTenantForm({ ...tenantForm, airport_pickup_surcharge: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">附加乘客加價（元/人）</label>
                <input
                  type="number"
                  min={0}
                  value={tenantForm.extra_passenger_surcharge}
                  onChange={(e) => setTenantForm({ ...tenantForm, extra_passenger_surcharge: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">舉牌加價（元）</label>
                <input
                  type="number"
                  min={0}
                  value={tenantForm.name_board_price}
                  onChange={(e) => setTenantForm({ ...tenantForm, name_board_price: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">兒童座椅加價（元/個）</label>
                <input
                  type="number"
                  min={0}
                  value={tenantForm.child_seat_price}
                  onChange={(e) => setTenantForm({ ...tenantForm, child_seat_price: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">增高墊加價（元/個）</label>
                <input
                  type="number"
                  min={0}
                  value={tenantForm.booster_seat_price}
                  onChange={(e) => setTenantForm({ ...tenantForm, booster_seat_price: parseInt(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleTenantSubmit()}
                disabled={submitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? '儲存中...' : '儲存加價設定'}
              </button>
              <p className="text-xs text-gray-400">以上加價金額用於公用訂車表單 V2 的即時報價</p>
            </div>

            {(() => {
              const rawRegions = [...new Set(priceRules.map(r => r.region))]
              const regionGroups = groupRegionsByCounty(rawRegions)
              const rawCounties = [...regionGroups.keys()].sort((a, b) => a === OTHER_COUNTY ? 1 : b === OTHER_COUNTY ? -1 : 0)
              if (rawCounties.length === 0) return null
              const counties = sortByPriority(rawCounties, regionOrder.counties)
              const districtObjs = regionOrderCounty
                ? sortByPriority(regionGroups.get(regionOrderCounty) || [], regionOrder.districts[regionOrderCounty] || [], d => d.district)
                : []

              const moveCounty = (index: number, direction: 'up' | 'down') => {
                const newIndex = direction === 'up' ? index - 1 : index + 1
                if (newIndex < 0 || newIndex >= counties.length) return
                const reordered = [...counties]
                ;[reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]]
                setRegionOrder({ ...regionOrder, counties: reordered })
              }

              const moveDistrict = (index: number, direction: 'up' | 'down') => {
                const newIndex = direction === 'up' ? index - 1 : index + 1
                if (newIndex < 0 || newIndex >= districtObjs.length) return
                const reorderedNames = districtObjs.map(d => d.district)
                ;[reorderedNames[index], reorderedNames[newIndex]] = [reorderedNames[newIndex], reorderedNames[index]]
                setRegionOrder({ ...regionOrder, districts: { ...regionOrder.districts, [regionOrderCounty]: reorderedNames } })
              }

              return (
                <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">公用區域設定（調整 V2 詢價表單縣市/鄉鎮區顯示順序）</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">用上下箭頭調整縣市顯示順序；點一個縣市可以再調整它底下鄉鎮區的順序，客戶端會照這個順序顯示。</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">縣市順序</p>
                      {counties.map((county, idx) => (
                        <div
                          key={county}
                          onClick={() => setRegionOrderCounty(county)}
                          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                            regionOrderCounty === county ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          <span className="text-gray-800 dark:text-gray-200">{county}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={(e) => { e.stopPropagation(); moveCounty(idx, 'up') }} disabled={idx === 0}
                              className="rounded bg-gray-100 px-1.5 py-1 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 transition-colors"><ArrowUp className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); moveCounty(idx, 'down') }} disabled={idx === counties.length - 1}
                              className="rounded bg-gray-100 px-1.5 py-1 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 transition-colors"><ArrowDown className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {regionOrderCounty ? `${regionOrderCounty} 的鄉鎮區順序` : '點左邊縣市可調整鄉鎮區順序'}
                      </p>
                      {districtObjs.map((d, idx) => (
                        <div key={d.district} className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm">
                          <span className="text-gray-800 dark:text-gray-200">{d.district}</span>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => moveDistrict(idx, 'up')} disabled={idx === 0}
                              className="rounded bg-gray-100 px-1.5 py-1 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 transition-colors"><ArrowUp className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => moveDistrict(idx, 'down')} disabled={idx === districtObjs.length - 1}
                              className="rounded bg-gray-100 px-1.5 py-1 text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 transition-colors"><ArrowDown className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTenantSubmit()}
                    disabled={submitting}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? '儲存中...' : '儲存區域順序'}
                  </button>
                </div>
              )
            })()}

            <div className="space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                支援兩種 CSV：(1) 窄表：機場,區域,車型,價格 每列一筆；(2) 縣市價目表（寬表）：縣市,鄉鎮區,車型1價格,車型2價格...，
                鄉鎮區欄位可用「、」分隔多個區一起套同一個價格。<b>寬表沒有機場欄位</b>，匯入時要先勾選下面要套用的機場。
              </p>
              <div className="flex flex-wrap gap-3">
                {AIRPORTS.map(a => (
                  <label key={a} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importAirports.includes(a)}
                      onChange={(e) => setImportAirports(prev => e.target.checked ? [...prev, a] : prev.filter(x => x !== a))}
                      className="rounded border-gray-300"
                    />
                    {a}
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (!file || !currentTenant) return
                    setImportingCsv(true)
                    setImportResult(null)
                    try {
                      const result = await tenantApi.importPriceRules(currentTenant.id, file, importAirports)
                      setImportResult(result)
                      const refreshed = await tenantApi.getPriceRules(currentTenant.id)
                      setPriceRules(refreshed)
                    } catch (error: any) {
                      setMessage({ type: 'error', text: error?.response?.data?.detail || 'CSV 匯入失敗' })
                    } finally {
                      setImportingCsv(false)
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={importingCsv}
                  onClick={() => csvInputRef.current?.click()}
                  className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-600 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
                >
                  {importingCsv ? '匯入中...' : '匯入 CSV'}
                </button>
                <span className="text-xs text-gray-400">同機場+區域+車型已存在會更新價格，不存在會新增，立即生效不用按「儲存設定」</span>
              </div>
            </div>
            {importResult && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3 text-xs space-y-1">
                <p className="text-gray-700 dark:text-gray-300">匯入完成：新增 {importResult.created} 筆、更新 {importResult.updated} 筆</p>
                {importResult.errors.length > 0 && (
                  <ul className="text-red-500 list-disc list-inside space-y-0.5">
                    {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <select
                value={priceFilterAirport}
                onChange={(e) => setPriceFilterAirport(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
              >
                <option value="">全部機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <input
                type="text"
                value={priceFilterRegion}
                onChange={(e) => setPriceFilterRegion(e.target.value)}
                placeholder="篩選區域關鍵字"
                className="w-36 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
              />
              <select
                value={priceFilterVehicleType}
                onChange={(e) => setPriceFilterVehicleType(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
              >
                <option value="">全部車型</option>
                <option value="__generic__">（通用）</option>
                {priceRuleVehicleTypeOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              {(priceFilterAirport || priceFilterRegion || priceFilterVehicleType) && (
                <button
                  type="button"
                  onClick={() => { setPriceFilterAirport(''); setPriceFilterRegion(''); setPriceFilterVehicleType('') }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  清除篩選
                </button>
              )}
              <span className="text-xs text-gray-400">共 {filteredPriceRules.length} / {priceRules.length} 筆</span>
            </div>

            <div className="space-y-2">
              {filteredPriceRules.map((rule) => (
                editingRuleId === rule.id ? (
                  <div key={rule.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/20 px-3 py-2">
                    <select
                      value={editingRule.airport}
                      onChange={(e) => setEditingRule({ ...editingRule, airport: e.target.value })}
                      className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      {AIRPORTS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input
                      type="text"
                      value={editingRule.region}
                      onChange={(e) => setEditingRule({ ...editingRule, region: e.target.value })}
                      className="flex-1 min-w-[100px] rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <select
                      value={editingRule.vehicle_type}
                      onChange={(e) => setEditingRule({ ...editingRule, vehicle_type: e.target.value })}
                      className="w-28 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">車型（通用）</option>
                      {tenantForm.vehicle_categories.filter((c) => c.name).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={editingRule.price || ''}
                      onChange={(e) => setEditingRule({ ...editingRule, price: parseInt(e.target.value) || 0 })}
                      className="w-24 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      disabled={editingRuleSubmitting}
                      onClick={async () => {
                        if (!currentTenant) return
                        setEditingRuleSubmitting(true)
                        try {
                          const updated = await tenantApi.updatePriceRule(currentTenant.id, rule.id, {
                            ...editingRule,
                            vehicle_type: editingRule.vehicle_type || undefined,
                          })
                          setPriceRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)))
                          setEditingRuleId(null)
                        } catch {
                          setMessage({ type: 'error', text: '更新牌價失敗' })
                        } finally {
                          setEditingRuleSubmitting(false)
                        }
                      }}
                      className="rounded-lg bg-blue-600 px-2 py-1.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {editingRuleSubmitting ? '儲存中...' : '儲存'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingRuleId(null)}
                      className="rounded-lg bg-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 transition-colors"
                    >
                      取消
                    </button>
                  </div>
                ) : (
                  <div key={rule.id} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-3 py-2 text-sm">
                    <span className="flex-1 text-gray-900 dark:text-white">{rule.airport}</span>
                    <span className="flex-1 text-gray-900 dark:text-white">{rule.region}</span>
                    <span className="w-24 text-gray-500 dark:text-gray-400">{rule.vehicle_type || '（通用）'}</span>
                    <span className="w-20 font-medium text-gray-900 dark:text-white">${rule.price}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingRuleId(rule.id)
                        setEditingRule({ airport: rule.airport, region: rule.region, vehicle_type: rule.vehicle_type || '', price: rule.price })
                      }}
                      className="rounded-lg bg-gray-100 px-2 py-1 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors text-xs"
                    >
                      編輯
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!currentTenant) return
                        try {
                          await tenantApi.deletePriceRule(currentTenant.id, rule.id)
                          setPriceRules((prev) => prev.filter((r) => r.id !== rule.id))
                        } catch {
                          setMessage({ type: 'error', text: '刪除牌價失敗' })
                        }
                      }}
                      className="rounded-lg bg-red-100 px-2 py-1 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900 transition-colors text-xs"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )
              ))}
              {filteredPriceRules.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {priceRules.length === 0 ? '尚未設定任何牌價' : '沒有符合篩選條件的牌價'}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={newPriceRule.airport}
                  onChange={(e) => setNewPriceRule({ ...newPriceRule, airport: e.target.value })}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {AIRPORTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <input
                  type="text"
                  value={newPriceRule.region}
                  onChange={(e) => setNewPriceRule({ ...newPriceRule, region: e.target.value })}
                  placeholder="區域，例：新竹市東區"
                  className="flex-1 min-w-[140px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <select
                  value={newPriceRule.vehicle_type}
                  onChange={(e) => setNewPriceRule({ ...newPriceRule, vehicle_type: e.target.value })}
                  className="w-28 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">車型（通用）</option>
                  {tenantForm.vehicle_categories.filter((c) => c.name).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
                <input
                  type="number"
                  min={0}
                  value={newPriceRule.price || ''}
                  onChange={(e) => setNewPriceRule({ ...newPriceRule, price: parseInt(e.target.value) || 0 })}
                  placeholder="價格"
                  className="w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  disabled={priceRuleSubmitting || !newPriceRule.region.trim() || !newPriceRule.price}
                  onClick={async () => {
                    if (!currentTenant) return
                    setPriceRuleSubmitting(true)
                    try {
                      const vehicle_type = newPriceRule.vehicle_type || undefined
                      const isTaoyuan = newPriceRule.airport === '桃園機場一航廈' || newPriceRule.airport === '桃園機場二航廈'
                      const airports = applyBothTerminals && isTaoyuan
                        ? ['桃園機場一航廈', '桃園機場二航廈']
                        : [newPriceRule.airport]
                      const created = await Promise.all(
                        airports.map((airport) => tenantApi.createPriceRule(currentTenant.id, { ...newPriceRule, airport, vehicle_type }))
                      )
                      setPriceRules((prev) => [...prev, ...created].sort((a, b) => a.airport.localeCompare(b.airport) || a.region.localeCompare(b.region)))
                      setNewPriceRule({ airport: AIRPORTS[0], region: '', vehicle_type: '', price: 0 })
                    } catch {
                      setMessage({ type: 'error', text: '新增牌價失敗' })
                    } finally {
                      setPriceRuleSubmitting(false)
                    }
                  }}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {priceRuleSubmitting ? '新增中...' : '+ 新增'}
                </button>
              </div>
              {(newPriceRule.airport === '桃園機場一航廈' || newPriceRule.airport === '桃園機場二航廈') && (
                <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyBothTerminals}
                    onChange={(e) => setApplyBothTerminals(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  同時套用到一航廈+二航廈（一次建立兩筆相同價格）
                </label>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}